import { NEED_KEYS, NEED_LABEL, bookingMarks, type NeedKey } from '@fieldops/shared'
import { createContext, useContext, type ReactNode } from 'react'
import type { BoardVisit } from './types.js'

/**
 * The board's half of decision 104: which need the board is showing, and how
 * many bookings have each. The rules are `bookingMarks` in `@fieldops/shared`;
 * the tile draws them as tags through `bookingLook` (decision 105), so the
 * step bar and its legend are gone.
 */

/* -------------------------------------------------------------------------- */
/* The Needs doing strip's filter                                              */
/* -------------------------------------------------------------------------- */

/**
 * Which need the board is showing, if any. Bookings without it fade, the way a
 * board search fades them: the day keeps its shape and the ones that need
 * something stand out. By context, like the search, so no board or lane in
 * between has to carry it.
 */
const BoardNeedContext = createContext<NeedKey | null>(null)

export function BoardNeedProvider({ need, children }: { need: NeedKey | null; children: ReactNode }) {
  return <BoardNeedContext.Provider value={need}>{children}</BoardNeedContext.Provider>
}

export function useBoardNeed(): NeedKey | null {
  return useContext(BoardNeedContext)
}

export interface NeedCount {
  key: NeedKey
  label: string
  count: number
  /** Whether any of them is late or refused, which colours the count red. */
  urgent: boolean
}

/** How many bookings in the window need each thing, in the order of the work. */
export function needCounts(visits: BoardVisit[], now: Date = new Date()): NeedCount[] {
  const tally = new Map<NeedKey, { count: number; urgent: boolean }>()
  for (const visit of visits) {
    const need = bookingMarks(visit, now).need
    if (!need) continue
    const entry = tally.get(need.key) ?? { count: 0, urgent: false }
    entry.count += 1
    entry.urgent ||= need.severity === 'problem'
    tally.set(need.key, entry)
  }
  return NEED_KEYS.flatMap((key) => {
    const entry = tally.get(key)
    return entry ? [{ key, label: NEED_LABEL[key], ...entry }] : []
  })
}
